import { useState, useRef } from "react";
import { Upload, X, ImageIcon } from "lucide-react";
import { getToken } from "@/lib/templateApi";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImageUpload({ slug, value, onChange, label = "Image", help }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError("");

    if (file.size > 10 * 1024 * 1024) {
      setError("File too large. Maximum size is 10 MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file (JPEG, PNG, or WebP).");
      return;
    }

    setUploading(true);
    setProgress(0);

    const form = new FormData();
    form.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    });

    xhr.addEventListener("load", () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          onChange(data.url);
        } catch {
          setError("Upload succeeded but response was invalid.");
        }
      } else {
        try {
          const body = JSON.parse(xhr.responseText);
          setError(body.detail || "Upload failed. Please try again.");
        } catch {
          setError(`Upload failed (${xhr.status}). Please try again.`);
        }
      }
    });

    xhr.addEventListener("error", () => {
      setUploading(false);
      setError("Network error. Check your connection and try again.");
    });

    xhr.addEventListener("abort", () => {
      setUploading(false);
      setError("Upload cancelled.");
    });

    xhr.open("POST", `${API}/api/template/${slug}/admin/upload`);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);
    xhr.send(form);
  };

  return (
    <div data-testid="image-upload">
      <label className="text-sm font-medium text-slate-300 block mb-1">{label}</label>

      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#1E293B]">
          <img src={value} alt="" className="w-full h-40 object-cover" />
          <div className="absolute top-2 right-2 flex gap-1">
            <button onClick={() => inputRef.current?.click()} className="bg-black/60 text-white p-1.5 rounded-md hover:bg-black/80 transition-colors" title="Replace image">
              <Upload size={13} />
            </button>
            <button onClick={() => onChange("")} className="bg-black/60 text-white p-1.5 rounded-md hover:bg-black/80 transition-colors" title="Remove image">
              <X size={13} />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full h-32 border-2 border-dashed border-white/10 rounded-lg flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-orange-500/30 hover:text-slate-400 transition-colors disabled:opacity-50"
          data-testid="image-upload-trigger"
        >
          {uploading ? (
            <div className="w-full px-8 space-y-2">
              <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                <div className="bg-orange-500 h-full rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-slate-400">{progress}% uploading...</span>
            </div>
          ) : (
            <>
              <ImageIcon size={20} />
              <span className="text-xs">Click to upload</span>
            </>
          )}
        </button>
      )}

      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />

      {error && (
        <div className="mt-1.5 bg-red-900/20 border border-red-800/30 text-red-400 px-3 py-2 rounded-md text-xs" data-testid="image-upload-error">
          {error}
        </div>
      )}
      {help && !error && <p className="text-[11px] text-slate-600 mt-1">{help}</p>}
    </div>
  );
}
